import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Paperclip, X, Loader2, FileText, Film, ImageIcon, Music } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCEPT_TYPES = '.jpg,.jpeg,.png,.gif,.webp,.heic,.mp4,.mov,.avi,.webm,.mkv,.mp3,.wav,.ogg,.m4a,.pdf';

const FILE_ICONS = {
  image: ImageIcon,
  video: Film,
  audio: Music,
  document: FileText,
  file: FileText,
};

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileAttachment({ onFileUploaded, getAuthHeader, disabled }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (25MB)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25MB.');
      return;
    }

    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/media/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setProgress(pct);
        },
      });

      const { file_url, file_name, file_type, file_size } = response.data;

      setPreview({ file_url, file_name, file_type, file_size });
      onFileUploaded({ file_url, file_name, file_type, file_size });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      // Reset input so the same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const cancelPreview = () => {
    setPreview(null);
  };

  const Icon = preview ? FILE_ICONS[preview.file_type] || FileText : Paperclip;

  if (uploading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-stone-100 rounded-full">
        <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
        <span className="text-xs text-stone-600">{progress}%</span>
        <div className="w-16 h-1.5 bg-stone-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-500 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  }

  if (preview) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-full max-w-[200px]">
        <Icon className="w-4 h-4 text-purple-500 flex-shrink-0" />
        <span className="text-xs text-purple-700 truncate">{preview.file_name}</span>
        <span className="text-xs text-purple-400">{formatFileSize(preview.file_size)}</span>
        <button
          type="button"
          onClick={cancelPreview}
          className="p-0.5 hover:bg-purple-100 rounded-full text-purple-400"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_TYPES}
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-attachment-input"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-500 hover:text-stone-700 disabled:opacity-50"
        title="Attach file"
        data-testid="file-attachment-btn"
      >
        <Paperclip className="w-5 h-5" />
      </button>
    </>
  );
}
